// The board primitives, shared by the client game model, the hint engine, and the offline
// puzzle analyzer. Kept in src/shared/ so the server tsconfig can compile the rule engine
// (tsconfig.server.json only includes src/server/** and src/shared/**).
//
// Nothing here may import from src/client/**.

export type CellState = null | 'X' | 'queen';

/** Canonical cell key. Every cell set in the codebase is a Set<string> of these. */
export const cellKey = (r: number, c: number): string => `${r},${c}`;

export function parseCellKey(key: string): [number, number] {
  const i = key.indexOf(',');
  return [Number(key.slice(0, i)), Number(key.slice(i + 1))];
}

export function cloneBoard(board: CellState[][]): CellState[][] {
  return board.map(row => [...row]);
}

// Cells a queen at (r,c) would attack: its whole row and column, the eight immediate
// diagonal neighbours, and every other cell of its color region. May repeat keys — callers
// collect into a Set. Shared by computeAutoXs and the hint engine.
export function queenAttacks(r: number, c: number, colorRegions: string[][]): string[] {
  const size = colorRegions.length;
  const color = colorRegions[r][c];
  const cells: string[] = [];
  for (let i = 0; i < size; i++) {
    if (i !== c) cells.push(`${r},${i}`);
    if (i !== r) cells.push(`${i},${c}`);
  }
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      if (dr === 0 && dc === 0) continue;
      const nr = r + dr, nc = c + dc;
      if (nr >= 0 && nr < size && nc >= 0 && nc < size) cells.push(`${nr},${nc}`);
    }
  }
  for (let r2 = 0; r2 < size; r2++) {
    for (let c2 = 0; c2 < size; c2++) {
      if (colorRegions[r2][c2] === color && !(r2 === r && c2 === c)) cells.push(`${r2},${c2}`);
    }
  }
  return cells;
}

export function computeAutoXs(board: CellState[][], colorRegions: string[][]): Set<string> {
  const cells = new Set<string>();
  const size = board.length;
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (board[r][c] !== 'queen') continue;
      for (const key of queenAttacks(r, c, colorRegions)) cells.add(key);
    }
  }
  return cells;
}
